import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ReactDOM from "react-dom";
import {
  updateUser,
  getUserDetails,
  getRoles,
} from "../../../redux/Slices/UsersSlice";
import { FaArrowRight, FaCheck } from "react-icons/fa";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useTitle } from "../../../shared/hooks/TitleContext";
import i18n from "../../../i18n/i18n";
import axios from "axios";
import {
  BASE_URL,
  getToken,
  getCurrentLanguage,
} from "../../../shared/utils.js/utils";

const EditUser = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { setTitle } = useTitle();
  const { t } = useTranslation("global");
  const isRTL = i18n.language === "ar";
  const textAlign = isRTL ? "right" : "left";
  const dir = isRTL ? "rtl" : "ltr";

  const [formData, setFormData] = useState({
    name: "",
    username: "",
    email: "",
    role_id: "",
    is_active: true,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(true);
  const [roles, setRoles] = useState([]);
  const [selectedRoleName, setSelectedRoleName] = useState("");
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [roleSearchTerm, setRoleSearchTerm] = useState("");
  const [roleCurrentPage, setRoleCurrentPage] = useState(1);
  const [rolesPagination, setRolesPagination] = useState(null);
  const [roleDropdownPosition, setRoleDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const roleSearchInputRef = useRef(null);
  const roleDropdownToggleRef = useRef(null);
  const roleSearchTimeoutRef = useRef(null);
  const [usernameStatus, setUsernameStatus] = useState("idle"); // idle | checking | available | exists | error
  const [usernameMessage, setUsernameMessage] = useState("");
  const [usernameSuggestions, setUsernameSuggestions] = useState([]);
  const usernameCheckTimeoutRef = useRef(null);

  useEffect(() => {
    setTitle("users.edit");
  }, [setTitle]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        setIsLoadingDetails(true);
        const result = await dispatch(getUserDetails(id));
        if (getUserDetails.fulfilled.match(result)) {
          const response = result.payload;
          const user = response?.data || response;
          const userRole =
            user.roles && user.roles.length > 0 ? user.roles[0] : null;
          setFormData({
            name: user.name || "",
            username: user.username || "",
            email: user.email || "",
            role_id: userRole ? userRole.id : "",
            is_active: user.is_active !== undefined ? user.is_active : true,
          });
          setSelectedRoleName(userRole ? userRole.name : "");
        } else {
          toast.error(
            result.payload ||
              t("users.details.error", {
                defaultValue: "فشل تحميل تفاصيل المستخدم",
              }),
            { rtl: isRTL },
          );
          navigate("/users");
        }
      } catch (err) {
        toast.error(
          err.message ||
            t("users.details.error", {
              defaultValue: "فشل تحميل تفاصيل المستخدم",
            }),
          { rtl: isRTL },
        );
        navigate("/users");
      } finally {
        setIsLoadingDetails(false);
      }
    };
    if (id) {
      fetchUserDetails();
    }
  }, [dispatch, id, navigate, t, isRTL]);

  const fetchRoles = async (
    page = roleCurrentPage,
    search = roleSearchTerm,
  ) => {
    try {
      const params = {
        page,
        per_page: 10,
      };
      if (search) params.search = search;
      const result = await dispatch(getRoles(params));
      if (getRoles.fulfilled.match(result)) {
        const response = result.payload;
        const data = response?.data || response;
        const rolesList = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
            ? data
            : [];
        setRoles(rolesList);
        const paginationData = data?.data
          ? {
              current_page: data.current_page || 1,
              last_page: data.last_page || 1,
              per_page: data.per_page || 10,
              total: data.total || 0,
              from: data.from || 0,
              to: data.to || 0,
            }
          : data?.meta || data?.pagination || null;
        setRolesPagination(paginationData);
      }
    } catch (err) {
      console.error("Error fetching roles:", err);
    }
  };

  useEffect(() => {
    if (roleDropdownOpen) {
      fetchRoles(roleCurrentPage, roleSearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleCurrentPage, roleDropdownOpen]);

  useEffect(() => {
    if (roleSearchTerm !== undefined && roleDropdownOpen) {
      if (roleSearchTimeoutRef.current)
        clearTimeout(roleSearchTimeoutRef.current);
      roleSearchTimeoutRef.current = setTimeout(() => {
        setRoleCurrentPage(1);
        fetchRoles(1, roleSearchTerm);
      }, 300);
    }
    return () => {
      if (roleSearchTimeoutRef.current)
        clearTimeout(roleSearchTimeoutRef.current);
    };
  }, [roleSearchTerm, roleDropdownOpen]);

  useEffect(() => {
    if (roleDropdownOpen && roleSearchInputRef.current) {
      roleSearchInputRef.current.focus();
    }
  }, [roleDropdownOpen]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (name === "username") {
      const username = value.trim();
      if (usernameCheckTimeoutRef.current) {
        clearTimeout(usernameCheckTimeoutRef.current);
      }
      if (!username) {
        setUsernameStatus("idle");
        setUsernameMessage("");
        setUsernameSuggestions([]);
        return;
      }
      setUsernameStatus("checking");
      setUsernameMessage(
        t("employees.form.usernameMessages.checking", {
          defaultValue: "جارٍ التحقق...",
        }),
      );
      setUsernameSuggestions([]);
      usernameCheckTimeoutRef.current = setTimeout(() => {
        checkUsernameAvailability(username, true);
      }, 500);
    }
  };

  const isUsernameAvailable = async username => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        return false;
      }
      const response = await axios.post(
        `${BASE_URL}/auth/check-username`,
        { username },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      const exists = response?.data?.data?.exists;
      return !exists;
    } catch {
      return false;
    }
  };

  const generateUsernameSuggestions = async baseUsername => {
    const suggestions = [];
    let attempt = 1;
    const maxAttempts = 30;

    while (suggestions.length < 3 && attempt <= maxAttempts) {
      const candidate = `${baseUsername}${attempt}`;
      // Avoid suggesting the same username
      if (
        candidate !== baseUsername &&
        !(await isUsernameAvailable(candidate))
      ) {
        attempt += 1;
        continue;
      }
      if (!suggestions.includes(candidate)) {
        suggestions.push(candidate);
      }
      attempt += 1;
    }

    setUsernameSuggestions(suggestions);
  };

  const checkUsernameAvailability = async (
    username,
    withSuggestions = false,
  ) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        setUsernameStatus("error");
        setUsernameMessage(
          t("employees.form.validation.requiredFields", {
            defaultValue: "خطأ في التحقق",
          }),
        );
        return;
      }

      const response = await axios.post(
        `${BASE_URL}/auth/check-username`,
        { username },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      const exists = response?.data?.data?.exists;
      if (exists) {
        setUsernameStatus("exists");
        setUsernameMessage(
          t("employees.form.usernameMessages.exists", {
            defaultValue: "اسم المستخدم مستخدم بالفعل",
          }),
        );
        if (withSuggestions) {
          await generateUsernameSuggestions(username);
        }
      } else {
        setUsernameStatus("available");
        setUsernameMessage(
          t("employees.form.usernameMessages.available", {
            defaultValue: "اسم المستخدم متاح",
            username,
          }),
        );
        setUsernameSuggestions([]);
      }
    } catch {
      setUsernameStatus("error");
      setUsernameMessage(
        t("employees.form.usernameMessages.exists", {
          defaultValue: "اسم المستخدم مستخدم بالفعل",
        }),
      );
    }
  };

  const handleUsernameSuggestionClick = suggestion => {
    setFormData(prev => ({
      ...prev,
      username: suggestion,
    }));
    setUsernameSuggestions([]);
    checkUsernameAvailability(suggestion, false);
  };

  const handleRoleSelect = (roleId, roleName) => {
    setFormData(prev => ({
      ...prev,
      role_id: roleId,
    }));
    setSelectedRoleName(roleName);
    setRoleDropdownOpen(false);
  };

  const getSelectedRoleName = () => {
    if (!formData.role_id)
      return t("users.selectRole", { defaultValue: "اختر الدور" });
    const role = roles.find(r => r.id === formData.role_id);
    return role?.translation || role?.name || selectedRoleName || formData.role_id;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!formData.name) {
      toast.error(
        t("users.validation.name", { defaultValue: "الرجاء إدخال الاسم" }),
        { rtl: isRTL },
      );
      return;
    }

    if (!formData.email) {
      toast.error(
        t("users.validation.email", {
          defaultValue: "الرجاء إدخال البريد الإلكتروني",
        }),
        { rtl: isRTL },
      );
      return;
    }

    if (!formData.role_id) {
      toast.error(
        t("users.validation.role", { defaultValue: "الرجاء اختيار الدور" }),
        { rtl: isRTL },
      );
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        ...formData,
      };
      const result = await dispatch(
        updateUser({ userId: id, userData: payload }),
      );
      if (updateUser.fulfilled.match(result)) {
        toast.success(
          t("users.update.success", {
            defaultValue: "تم تحديث المستخدم بنجاح",
          }),
          { rtl: isRTL },
        );
        navigate("/users");
      } else {
        toast.error(
          result.payload ||
            t("users.update.error", { defaultValue: "فشل تحديث المستخدم" }),
          { rtl: isRTL },
        );
      }
    } catch (err) {
      toast.error(
        err.message ||
          t("users.update.error", { defaultValue: "فشل تحديث المستخدم" }),
        { rtl: isRTL },
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingDetails) {
    return (
      <div
        style={{
          padding: "30px",
          backgroundColor: "#14263b",
          minHeight: "calc(100vh - 80px)",
          color: "white",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          direction: dir,
          textAlign,
        }}
      >
        {t("common.loading", { defaultValue: "جاري التحميل..." })}
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "30px",
        backgroundColor: "#14263b",
        minHeight: "calc(100vh - 80px)",
        color: "white",
        direction: dir,
        textAlign,
      }}
    >
      <div style={{ marginBottom: "30px" }}>
        <button
          onClick={() => navigate("/users")}
          style={{
            backgroundColor: "#666",
            color: "white",
            border: "none",
            padding: "10px",
            borderRadius: "8px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "20px",
            fontSize: "14px",
            fontFamily:
              '"Times New Roman Bold", "Times New Roman", "Times", serif',
            flexDirection: isRTL ? "row" : "row-reverse",
          }}
        >
          <FaArrowRight />
          {t("common.back", { defaultValue: "الرجوع" })}
        </button>
        <h1
          style={{
            fontSize: "24px",
            fontWeight: "bold",
            margin: 0,
            fontFamily:
              '"Times New Roman Bold", "Times New Roman", "Times", serif',
          }}
        >
          {t("users.edit", { defaultValue: "تعديل مستخدم" })}
        </h1>
      </div>

      <form
        onSubmit={handleSubmit}
        style={{
          backgroundColor: "#1b3350",
          border: "1px solid #333",
          borderRadius: "12px",
          padding: "32px",
          width: "100%",
          maxWidth: "100%",
        }}
      >
        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              color: "white",
              fontFamily:
                '"Times New Roman Bold", "Times New Roman", "Times", serif',
            }}
          >
            {t("labels.name", { defaultValue: "الاسم" })}{" "}
            <span style={{ color: "#dc3545" }}>*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            style={{
              width: "100%",
              padding: "12px",
              backgroundColor: "#1a1f2e",
              border: "1px solid #333",
              borderRadius: "8px",
              color: "white",
              fontSize: "14px",
              boxSizing: "border-box",
              direction: dir,
              fontFamily:
                '"Times New Roman Bold", "Times New Roman", "Times", serif',
            }}
          />
        </div>

        {/* اسم المستخدم */}
        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              color: "white",
              fontFamily:
                '"Times New Roman Bold", "Times New Roman", "Times", serif',
            }}
          >
            {t("employees.form.fields.username", {
              defaultValue: "اسم المستخدم",
            })}{" "}
            <span
              style={{ color: "white", fontSize: "12px", fontWeight: "normal" }}
            >
              {t("employees.form.optional", { defaultValue: "(اختياري)" })}
            </span>
          </label>
          <input
            type="text"
            name="username"
            value={formData.username}
            onChange={handleChange}
            placeholder={t("employees.form.placeholders.username", {
              defaultValue: "أدخل اسم المستخدم",
            })}
            style={{
              width: "100%",
              padding: "12px",
              backgroundColor: "#1a1f2e",
              border: "1px solid #333",
              borderRadius: "8px",
              color: "white",
              fontSize: "14px",
              boxSizing: "border-box",
              direction: dir,
              fontFamily:
                '"Times New Roman Bold", "Times New Roman", "Times", serif',
            }}
          />
          {usernameMessage && (
            <div
              style={{
                marginTop: "6px",
                fontSize: "12px",
                color:
                  usernameStatus === "available"
                    ? "#10b981"
                    : usernameStatus === "checking"
                      ? "#eab308"
                      : "#f97373",
              }}
            >
              {usernameMessage}
            </div>
          )}
          {usernameSuggestions.length > 0 && (
            <div style={{ marginTop: "8px" }}>
              <div
                style={{
                  marginBottom: "4px",
                  fontSize: "12px",
                  color: "#9ca3af",
                }}
              >
                {t("employees.form.usernameMessages.suggestionsTitle", {
                  defaultValue: "اقتراحات:",
                })}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {usernameSuggestions.map(suggestion => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => handleUsernameSuggestionClick(suggestion)}
                    style={{
                      padding: "6px 10px",
                      backgroundColor: "#374151",
                      borderRadius: "999px",
                      border: "none",
                      color: "white",
                      fontSize: "12px",
                      cursor: "pointer",
                    }}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              color: "white",
              fontFamily:
                '"Times New Roman Bold", "Times New Roman", "Times", serif',
            }}
          >
            {t("sign.email", { defaultValue: "البريد الإلكتروني" })}{" "}
            <span style={{ color: "#dc3545" }}>*</span>
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            style={{
              width: "100%",
              padding: "12px",
              backgroundColor: "#1a1f2e",
              border: "1px solid #333",
              borderRadius: "8px",
              color: "white",
              fontSize: "14px",
              boxSizing: "border-box",
              direction: dir,
              fontFamily:
                '"Times New Roman Bold", "Times New Roman", "Times", serif',
            }}
          />
        </div>

        <div style={{ marginBottom: "20px", position: "relative" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              color: "white",
              fontFamily:
                '"Times New Roman Bold", "Times New Roman", "Times", serif',
            }}
          >
            {t("users.role", { defaultValue: "الدور" })}{" "}
            <span style={{ color: "#dc3545" }}>*</span>
          </label>
          <div
            ref={roleDropdownToggleRef}
            onClick={() => {
              if (roleDropdownToggleRef.current) {
                const rect =
                  roleDropdownToggleRef.current.getBoundingClientRect();
                setRoleDropdownPosition({
                  top: rect.bottom + window.scrollY,
                  left: rect.left + window.scrollX,
                  width: rect.width,
                });
              }
              setRoleDropdownOpen(prev => !prev);
            }}
            style={{
              width: "100%",
              padding: "12px",
              backgroundColor: "#1a1f2e",
              border: "1px solid #333",
              borderRadius: "8px",
              color: "white",
              fontSize: "14px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              cursor: "pointer",
              boxSizing: "border-box",
              fontFamily:
                '"Times New Roman Bold", "Times New Roman", "Times", serif',
            }}
          >
            <span
              style={{
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: !formData.role_id ? "#888" : "white",
              }}
            >
              {getSelectedRoleName()}
            </span>
            <span style={{ fontSize: "12px", marginLeft: "10px" }}>▼</span>
          </div>
          {roleDropdownOpen &&
            ReactDOM.createPortal(
              <div
                style={{
                  position: "absolute",
                  top: `${roleDropdownPosition.top}px`,
                  left: `${roleDropdownPosition.left}px`,
                  width: `${roleDropdownPosition.width}px`,
                  backgroundColor: "#1a1f2e",
                  border: "1px solid #333",
                  borderRadius: "8px",
                  zIndex: 10000,
                  maxHeight: "400px",
                  overflowY: "auto",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.3)",
                  marginTop: "4px",
                }}
                onClick={e => e.stopPropagation()}
              >
                <input
                  ref={roleSearchInputRef}
                  type="text"
                  placeholder={t("common.search", {
                    defaultValue: isRTL ? "بحث..." : "Search...",
                  })}
                  value={roleSearchTerm}
                  onChange={e => setRoleSearchTerm(e.target.value)}
                  onClick={e => e.stopPropagation()}
                  style={{
                    width: "100%",
                    padding: "10px",
                    backgroundColor: "#2d3748",
                    border: "none",
                    borderBottom: "1px solid #333",
                    color: "white",
                    fontSize: "14px",
                    boxSizing: "border-box",
                    fontFamily:
                      '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  }}
                />
                {rolesPagination && rolesPagination.last_page >= 1 && (
                  <div
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #333",
                      background: "#14263b",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 6,
                      }}
                    >
                      <span
                        style={{
                          color: "#ccc",
                          fontSize: 11,
                          direction: isRTL ? "rtl" : "ltr",
                          textAlign: isRTL ? "right" : "left",
                        }}
                      >
                        {t("common.page", { defaultValue: "صفحة" })}{" "}
                        {rolesPagination.current_page}{" "}
                        {t("common.of", { defaultValue: "من" })}{" "}
                        {rolesPagination.last_page}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 4,
                        flexWrap: "wrap",
                      }}
                    >
                      <button
                        type="button"
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (rolesPagination.current_page > 1) {
                            setRoleCurrentPage(1);
                          }
                        }}
                        disabled={rolesPagination.current_page === 1}
                        style={{
                          padding: "4px 8px",
                          backgroundColor:
                            rolesPagination.current_page === 1
                              ? "#2a2a2a"
                              : "#1a1f2e",
                          color:
                            rolesPagination.current_page === 1
                              ? "#666"
                              : "white",
                          border: "1px solid #444",
                          borderRadius: "4px",
                          cursor:
                            rolesPagination.current_page === 1
                              ? "not-allowed"
                              : "pointer",
                          fontSize: 10,
                          fontWeight: "bold",
                          minWidth: "28px",
                        }}
                      >
                        ««
                      </button>
                      <button
                        type="button"
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (rolesPagination.current_page > 1) {
                            setRoleCurrentPage(
                              rolesPagination.current_page - 1,
                            );
                          }
                        }}
                        disabled={rolesPagination.current_page === 1}
                        style={{
                          padding: "4px 8px",
                          backgroundColor:
                            rolesPagination.current_page === 1
                              ? "#2a2a2a"
                              : "#1a1f2e",
                          color:
                            rolesPagination.current_page === 1
                              ? "#666"
                              : "white",
                          border: "1px solid #444",
                          borderRadius: "4px",
                          cursor:
                            rolesPagination.current_page === 1
                              ? "not-allowed"
                              : "pointer",
                          fontSize: 10,
                          fontWeight: "bold",
                          minWidth: "28px",
                        }}
                      >
                        ‹
                      </button>
                      {(() => {
                        const pages = [];
                        const current = rolesPagination.current_page;
                        const last = rolesPagination.last_page;
                        const showPages = 5;
                        let start = Math.max(
                          1,
                          current - Math.floor(showPages / 2),
                        );
                        let end = Math.min(last, start + showPages - 1);
                        if (end - start < showPages - 1)
                          start = Math.max(1, end - showPages + 1);
                        for (let i = start; i <= end; i++) pages.push(i);
                        return pages.map(page => (
                          <button
                            key={page}
                            type="button"
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              setRoleCurrentPage(page);
                            }}
                            style={{
                              padding: "4px 8px",
                              backgroundColor:
                                page === current ? "#AC2000" : "#1a1f2e",
                              color: "white",
                              border:
                                page === current
                                  ? "1px solid #AC2000"
                                  : "1px solid #444",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: 10,
                              fontWeight: page === current ? "bold" : "normal",
                              minWidth: "28px",
                            }}
                          >
                            {page}
                          </button>
                        ));
                      })()}
                      <button
                        type="button"
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (
                            rolesPagination.current_page <
                            rolesPagination.last_page
                          ) {
                            setRoleCurrentPage(
                              rolesPagination.current_page + 1,
                            );
                          }
                        }}
                        disabled={
                          rolesPagination.current_page ===
                          rolesPagination.last_page
                        }
                        style={{
                          padding: "4px 8px",
                          backgroundColor:
                            rolesPagination.current_page ===
                            rolesPagination.last_page
                              ? "#2a2a2a"
                              : "#1a1f2e",
                          color:
                            rolesPagination.current_page ===
                            rolesPagination.last_page
                              ? "#666"
                              : "white",
                          border: "1px solid #444",
                          borderRadius: "4px",
                          cursor:
                            rolesPagination.current_page ===
                            rolesPagination.last_page
                              ? "not-allowed"
                              : "pointer",
                          fontSize: 10,
                          fontWeight: "bold",
                          minWidth: "28px",
                        }}
                      >
                        ›
                      </button>
                      <button
                        type="button"
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (
                            rolesPagination.current_page <
                            rolesPagination.last_page
                          ) {
                            setRoleCurrentPage(rolesPagination.last_page);
                          }
                        }}
                        disabled={
                          rolesPagination.current_page ===
                          rolesPagination.last_page
                        }
                        style={{
                          padding: "4px 8px",
                          backgroundColor:
                            rolesPagination.current_page ===
                            rolesPagination.last_page
                              ? "#2a2a2a"
                              : "#1a1f2e",
                          color:
                            rolesPagination.current_page ===
                            rolesPagination.last_page
                              ? "#666"
                              : "white",
                          border: "1px solid #444",
                          borderRadius: "4px",
                          cursor:
                            rolesPagination.current_page ===
                            rolesPagination.last_page
                              ? "not-allowed"
                              : "pointer",
                          fontSize: 10,
                          fontWeight: "bold",
                          minWidth: "28px",
                        }}
                      >
                        »»
                      </button>
                    </div>
                  </div>
                )}
                <div
                  style={{
                    maxHeight: "200px",
                    overflowY: "auto",
                    overflowX: "hidden",
                    paddingBottom: "8px",
                  }}
                >
                  {roles.length > 0 ? (
                    roles.map((role, index) => {
                      const isSelected = formData.role_id === role.id;
                      const isLast = index === roles.length - 1;
                      return (
                        <div
                          key={role.id}
                          onClick={() => handleRoleSelect(role.id, role.translation || role.name)}
                          style={{
                            padding: "10px",
                            paddingBottom: isLast ? "18px" : "10px",
                            cursor: "pointer",
                            backgroundColor: isSelected
                              ? "#2a2a2a"
                              : "transparent",
                            color: isSelected ? "#AC2000" : "white",
                            borderBottom: isLast ? "none" : "1px solid #333",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            fontFamily:
                              '"Times New Roman Bold", "Times New Roman", "Times", serif',
                          }}
                          onMouseEnter={e => {
                            if (!isSelected) {
                              e.target.style.backgroundColor = "#374151";
                            }
                          }}
                          onMouseLeave={e => {
                            if (!isSelected) {
                              e.target.style.backgroundColor = "transparent";
                            }
                          }}
                        >
                          <div
                            style={{
                              width: "18px",
                              height: "18px",
                              border: "2px solid #AC2000",
                              borderRadius: "4px",
                              backgroundColor: isSelected
                                ? "#AC2000"
                                : "transparent",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            {isSelected && (
                              <FaCheck
                                style={{ color: "white", fontSize: "12px" }}
                              />
                            )}
                          </div>
                          <span style={{ fontSize: "14px" }}>{role.translation || role.name}</span>
                        </div>
                      );
                    })
                  ) : (
                    <div
                      style={{
                        padding: "10px",
                        color: "#888",
                        textAlign: "center",
                      }}
                    >
                      {t("labels.noData", { defaultValue: "لا توجد نتائج" })}
                    </div>
                  )}
                </div>
              </div>,
              document.body,
            )}
          {roleDropdownOpen && (
            <div
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 999,
              }}
              onClick={() => {
                setRoleDropdownOpen(false);
              }}
            />
          )}
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontSize: "14px",
              fontWeight: "bold",
              color: "white",
              fontFamily:
                '"Times New Roman Bold", "Times New Roman", "Times", serif',
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              style={{
                width: "18px",
                height: "18px",
                cursor: "pointer",
              }}
            />
            {t("users.isActive", { defaultValue: "نشط" })}
          </label>
        </div>

        <div
          style={{
            display: "flex",
            gap: "15px",
            justifyContent: "center",
            flexDirection: isRTL ? "row-reverse" : "row",
          }}
        >
          <button
            type="submit"
            disabled={isLoading}
            style={{
              backgroundColor: isLoading ? "#666" : "#AC2000",
              color: "white",
              border: "none",
              padding: "12px 24px",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: isLoading ? "not-allowed" : "pointer",
              opacity: isLoading ? 0.6 : 1,
              fontFamily:
                '"Times New Roman Bold", "Times New Roman", "Times", serif',
            }}
          >
            {isLoading
              ? t("common.saving", { defaultValue: "جاري الحفظ..." })
              : t("btns.save", { defaultValue: "حفظ" })}
          </button>
          <button
            type="button"
            onClick={() => navigate("/users")}
            style={{
              backgroundColor: "#6c757d",
              color: "white",
              border: "none",
              padding: "12px 24px",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: "pointer",
              fontFamily:
                '"Times New Roman Bold", "Times New Roman", "Times", serif',
            }}
          >
            {t("btns.cancel", { defaultValue: "إلغاء" })}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditUser;
